export const ALLOWED_AUDIO_TYPES = ["audio/mpeg", "audio/wav", "audio/mp4", "audio/m4a", "audio/ogg", "audio/webm"];
export const ALLOWED_AUDIO_EXTENSIONS = ["mp3", "wav", "m4a", "ogg", "webm"];

export const ALLOWED_DOCUMENT_TYPES = [
  "text/plain",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];
export const ALLOWED_DOCUMENT_EXTENSIONS = ["txt", "csv", "doc", "docx"];

export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
export const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

export const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-matroska"];
export const ALLOWED_VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "mkv"];

export const MAX_PDF_FILE_SIZE = 50 * 1024 * 1024;

// Supabase Storage's agent-documents bucket caps uploads at 50MB. Larger videos
// must be submitted as an external URL instead (fetched directly by the
// transcription service, never uploaded to Supabase Storage).
export const MAX_VIDEO_FILE_SIZE = 50 * 1024 * 1024;

// Audio, document and image uploads land in the same 50MB agent-documents
// bucket, so they need the same client-side gate PDF and video already have.
// Without it the file uploads for however long it takes and only fails at the
// storage layer, with no size shown up front.
export const MAX_UPLOAD_FILE_SIZE = 50 * 1024 * 1024;

const validateFileType = (
  file: File,
  allowedTypes: string[],
  allowedExtensions: string[],
): boolean => {
  if (allowedTypes.includes(file.type)) return true;
  const extension = file.name.split(".").pop()?.toLowerCase();
  return extension ? allowedExtensions.includes(extension) : false;
};

export const validateAudioFile = (file: File): boolean =>
  validateFileType(file, ALLOWED_AUDIO_TYPES, ALLOWED_AUDIO_EXTENSIONS);

export const validateDocumentFile = (file: File): boolean =>
  validateFileType(file, ALLOWED_DOCUMENT_TYPES, ALLOWED_DOCUMENT_EXTENSIONS);

export const validateImageFile = (file: File): boolean =>
  validateFileType(file, ALLOWED_IMAGE_TYPES, ALLOWED_IMAGE_EXTENSIONS);

export const validateVideoFile = (file: File): boolean =>
  validateFileType(file, ALLOWED_VIDEO_TYPES, ALLOWED_VIDEO_EXTENSIONS);

export const validatePdfFileSize = (file: File): boolean => file.size <= MAX_PDF_FILE_SIZE;

export const validateVideoFileSize = (file: File): boolean => file.size <= MAX_VIDEO_FILE_SIZE;

export const validateUploadFileSize = (file: File): boolean => file.size <= MAX_UPLOAD_FILE_SIZE;

export const getTooLargeMessage = (file: File): string =>
  `${file.name} is ${formatFileSize(file.size)} — files must be under ${formatFileSize(
    MAX_UPLOAD_FILE_SIZE,
  )}.`;

export const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }
  return `${(bytes / 1024).toFixed(2)} KB`;
};

export const getErrorMessage = (type: "audio" | "document" | "image" | "video"): string => {
  const messages = {
    audio: "Please select a valid audio file (MP3, WAV, M4A, OGG, or WebM)",
    document: "Please select a valid document file (TXT, CSV, DOC, or DOCX)",
    image: "Please select a valid image file (JPEG, PNG, GIF, or WebP)",
    video: `Please select a valid video file (MP4, MOV, WebM, or MKV), up to ${formatFileSize(
      MAX_VIDEO_FILE_SIZE,
    )}. For larger files, paste a video URL instead.`,
  };
  return messages[type];
};
